import { useEffect, useState, useCallback } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { http } from "@/api/client";

const GOOGLE_G = (
  <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
  </svg>
);

function Stars({ value, size = "w-4 h-4" }) {
  return (
    <span className="flex items-center gap-0.5 text-[#ffb020]">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`${size} ${i <= Math.round(value) ? "fill-current" : "fill-none opacity-30"}`} />
      ))}
    </span>
  );
}

export default function GoogleReviews() {
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    http.get("/google-reviews").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  const count = data?.reviews?.length || 0;
  const next = useCallback(() => setIdx((i) => (i + 1) % count), [count]);
  const prev = useCallback(() => setIdx((i) => (i - 1 + count) % count), [count]);

  useEffect(() => {
    if (count < 2) return;
    const t = setInterval(next, 7000);
    return () => clearInterval(t);
  }, [count, next]);

  if (!data) return null;

  const review = data.reviews?.[idx];

  return (
    <div className="mt-6 w-full max-w-xl mx-auto" data-testid="google-reviews-widget">
      <a
        href={data.maps_url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="google-reviews-summary"
        className="inline-flex items-center gap-2 text-sm text-[#5b6577] hover:text-[#00a86b] transition-colors"
      >
        {GOOGLE_G}
        <span className="font-headings font-bold text-[#1a2332]">{Number(data.rating).toFixed(1).replace(".", ",")}</span>
        <Stars value={data.rating} />
        <span>({data.total} avis Google)</span>
      </a>

      {review && (
        <div
          className="mt-4 relative bg-white border border-black/5 rounded-2xl shadow-sm px-5 py-4 text-left"
          data-testid="google-review-card"
        >
          <div className="flex items-center gap-3">
            {review.photo ? (
              <img src={review.photo} alt={review.author} className="w-9 h-9 rounded-full" referrerPolicy="no-referrer" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-[#00a86b]/10 flex items-center justify-center font-headings font-bold text-[#00a86b]">
                {review.author?.charAt(0)}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-headings font-bold text-sm text-[#1a2332] truncate">{review.author}</div>
              <div className="flex items-center gap-2">
                <Stars value={review.rating} size="w-3 h-3" />
                <span className="text-xs text-[#5b6577]">{review.relative_time}</span>
              </div>
            </div>
            {count > 1 && (
              <div className="flex items-center gap-1">
                <button onClick={prev} aria-label="Avis précédent" data-testid="google-review-prev" className="w-7 h-7 rounded-full border border-black/10 flex items-center justify-center text-[#5b6577] hover:text-[#00a86b] hover:border-[#00a86b] transition-colors">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button onClick={next} aria-label="Avis suivant" data-testid="google-review-next" className="w-7 h-7 rounded-full border border-black/10 flex items-center justify-center text-[#5b6577] hover:text-[#00a86b] hover:border-[#00a86b] transition-colors">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
          <p className="mt-3 text-sm text-[#5b6577] leading-relaxed line-clamp-3">{review.text}</p>
          {count > 1 && (
            <div className="mt-3 flex justify-center gap-1.5">
              {data.reviews.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  aria-label={`Avis ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-[#00a86b]" : "w-1.5 bg-black/15"}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
